import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ssoLinkAccountSchema, type SSOLinkAccountData } from '@/schemas/account';
import { authService } from '@/api';
import { useUI } from '@/hooks/ui/useUI';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import type { LC_GetAccountInfoOut } from '@/api/domains/auth/auth.types';


interface UseSSOLinkAccountReturn {
  register: ReturnType<typeof useForm<SSOLinkAccountData>>['register'];
  handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  errors: ReturnType<typeof useForm<SSOLinkAccountData>>['formState']['errors'];
  isSubmitting: boolean;
  isLoadingInfo: boolean;
  accountInfo: LC_GetAccountInfoOut | null;
  userNameTaken: boolean;
}

export const useSSOLinkAccount = (onSuccess: () => void): UseSSOLinkAccountReturn => {
  const { t: translate } = useTranslation('auth');
  const { showNotification, showApiError } = useUI();
  const [accountInfo, setAccountInfo] = useState<LC_GetAccountInfoOut | null>(null);
  const [isLoadingInfo, setIsLoadingInfo] = useState(true);
  const [userNameTaken, setUserNameTaken] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    setError,
  } = useForm<SSOLinkAccountData>({
    resolver: zodResolver(ssoLinkAccountSchema),
    mode: 'onBlur',
  });

  const loadAccountInfo = useCallback(async () => {
    setIsLoadingInfo(true);
    try {
      const response = await authService.getAccountInfo();
      if (response.success && response.data) {
        const info = response.data;
        setAccountInfo(info);
        setValue('userName', info.userName ?? '');
        setValue('rut', info.employeeTaxIdentificationNumber ?? '');
        setValue('email', info.email ?? '');
      } else {
        showApiError(response, translate('ssoLinking.errors.loadAccountInfo'));
      }
    } catch {
      setAccountInfo(null);
      showNotification('error', translate('ssoLinking.errors.loadAccountInfo'), 5000);
    } finally {
      setIsLoadingInfo(false);
    }
  }, [setValue, showApiError, showNotification, translate]);

  useEffect(() => {
    void loadAccountInfo();
  }, [loadAccountInfo]);

  const onSubmit = async (data: SSOLinkAccountData) => {
    setUserNameTaken(false);
    try {
      const response = await authService.linkAccount({
        userName: data.userName,
        password: data.password,
      });

      if (response.success) {
        onSuccess();
        return;
      }

      const errorCode = response.error?.code;

      if (errorCode === 'SSO_USERNAME_TAKEN') {
        setUserNameTaken(true);
        setError('userName', { message: response.error?.detail ?? translate('ssoLinking.errors.userNameTaken') });
        return;
      }

      if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
        injectValidationErrors(response.error.validationErrors, { setError });
        return;
      }

      showApiError(response, translate('ssoLinking.errors.linkAccount'));
    } catch {
      setUserNameTaken(false);
      showNotification('error', translate('ssoLinking.errors.linkAccount'), 5000);
    }
  };

  return {
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isSubmitting,
    isLoadingInfo,
    accountInfo,
    userNameTaken,
  };
};
